import React from 'react';
import { FilterState } from '../../types/album';
import { theme } from '../../styles/theme';

interface ActiveFiltersProps {
  filters: FilterState;
  onRemoveFilter: (type: 'genres' | 'decades' | 'countries' | 'languages' | 'artists', value: string) => void;
  onClearSearch: () => void;
  onToggleFilterMode: () => void;
  availableCountries?: Array<{ code: string; name: string; count: number }>;
  availableLanguages?: Array<{ code: string; name: string; count: number }>;
}

const ActiveFilters: React.FC<ActiveFiltersProps> = ({ 
  filters, 
  onRemoveFilter, 
  onClearSearch, 
  onToggleFilterMode,
  availableCountries = [],
  availableLanguages = []
}) => {
  const containerStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.surface,
    position: 'relative',
    zIndex: 100,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: theme.colors.text,
  };

  const toggleContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: theme.colors.text,
    cursor: 'pointer',
    userSelect: 'none',
  };

  const toggleSwitchStyle: React.CSSProperties = {
    position: 'relative',
    width: '36px',
    height: '18px',
    backgroundColor: filters.filterMode === 'any' ? theme.colors.border : theme.colors.primary,
    borderRadius: '12px',
    transition: `background-color ${theme.transitions.fast}`,
    border: 'none',
    cursor: 'pointer',
  };

  const toggleKnobStyle: React.CSSProperties = {
    position: 'absolute',
    top: '2px',
    left: filters.filterMode === 'any' ? '2px' : '20px',
    width: '14px',
    height: '14px',
    backgroundColor: 'white',
    borderRadius: '50%',
    transition: `left ${theme.transitions.fast}`,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  };

  const filtersRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    alignItems: 'center',
  };

  const chipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: theme.colors.primary,
    color: 'white',
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '500',
  };

  const removeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '0',
    fontSize: '1rem',
    lineHeight: '1',
  };

  const hasActiveFilters = filters.searchText || 
                        filters.genres.length > 0 || 
                        filters.decades.length > 0 || 
                        filters.countries.length > 0 || 
                        filters.languages.length > 0 ||
                        filters.artists.length > 0 ||
                        (filters.yearRange[0] !== 1950 || filters.yearRange[1] !== new Date().getFullYear());

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={titleStyle}>Active Filters:</div>
        {filters.genres.length > 0 && (
          <div 
            style={toggleContainerStyle}
            onClick={onToggleFilterMode}
            title={`Currently ${filters.filterMode.toUpperCase()} mode - click to switch`}
          >
            <span>ANY</span>
            <button style={toggleSwitchStyle}>
              <div style={toggleKnobStyle}></div>
            </button>
            <span>ALL</span>
          </div>
        )}
      </div>
      <div style={filtersRowStyle}>
        {filters.searchText && (
          <div style={chipStyle}>
            Search: "{filters.searchText}"
            <button style={removeButtonStyle} onClick={onClearSearch}>
              ×
            </button>
          </div>
        )}
        
        {filters.genres.map(genre => (
          <div key={genre} style={chipStyle}>
            {genre}
            <button 
              style={removeButtonStyle} 
              onClick={() => onRemoveFilter('genres', genre)}
            >
              ×
            </button>
          </div>
        ))}

        {filters.decades.map(decade => (
          <div key={decade} style={chipStyle}>
            {decade}
            <button 
              style={removeButtonStyle} 
              onClick={() => onRemoveFilter('decades', decade)}
            >
              ×
            </button>
          </div>
        ))}

        {(filters.yearRange[0] !== 1950 || filters.yearRange[1] !== 2024) && (
          <div style={chipStyle}>
            Years: {filters.yearRange[0]} - {filters.yearRange[1]}
          </div>
        )}

        {filters.artists.map(artist => (
          <div key={artist} style={chipStyle}>
            Artist: {artist}
            <button 
              style={removeButtonStyle} 
              onClick={() => onRemoveFilter('artists', artist)}
            >
              ×
            </button>
          </div>
        ))}

        {filters.countries.map(countryCode => {
          const country = availableCountries.find(c => c.code === countryCode);
          return (
            <div key={countryCode} style={chipStyle}>
              🌍 {country?.name || countryCode}
              <button 
                style={removeButtonStyle} 
                onClick={() => onRemoveFilter('countries', countryCode)}
              >
                ×
              </button>
            </div>
          );
        })}

        {filters.languages.map(languageCode => {
          const language = availableLanguages.find(l => l.code === languageCode);
          return (
            <div key={languageCode} style={chipStyle}>
              🗣️ {language?.name || languageCode}
              <button 
                style={removeButtonStyle} 
                onClick={() => onRemoveFilter('languages', languageCode)}
              >
                ×
              </button>
            </div>
          );
        })}
        
      </div>
    </div>
  );
};

export default ActiveFilters;